import { ProducerStatus } from './producer-admin';

export enum MealsPerDayAverage {
  Range0To50 = 'Range0To50',
  Range50To100 = 'Range50To100',
  Range100To150 = 'Range100To150',
  Range150To200 = 'Range150To200',
  Over200 = 'Over200'
}

export enum MealsPerDayBest {
  Range150To200 = 'Range150To200',
  Range200To250 = 'Range200To250',
  Range250To300 = 'Range250To300',
  Range300To400 = 'Range300To400',
  Over400 = 'Over400'
}

export enum AdditionalPresoldCapacity {
  UpTo100 = 'UpTo100',
  Range100To200 = 'Range100To200',
  Range200To300 = 'Range200To300',
  Range300To400 = 'Range300To400',
  Over400 = 'Over400'
}

export enum GoogleYelpRating {
  FourFiveOrHigher = 'FourFiveOrHigher',
  FourZeroToFourFour = 'FourZeroToFourFour',
  BelowFourZero = 'BelowFourZero',
  NotListed = 'NotListed'
}

export enum RatingImpactWillingness {
  GreatlyEnhance = 'GreatlyEnhance',
  StayAboutTheSame = 'StayAboutTheSame'
}

export enum CleanIngredientsWillingness {
  AlreadyCookThisWay = 'AlreadyCookThisWay',
  FewSmallSwaps = 'FewSmallSwaps',
  OpenWithWork = 'OpenWithWork',
  NotRightNow = 'NotRightNow'
}

export enum CertifiedItemCountComfort {
  EightToTwelveFeelsRight = 'EightToTwelveFeelsRight',
  CouldDoMoreThanTwelve = 'CouldDoMoreThanTwelve',
  TooMany = 'TooMany',
  NotSure = 'NotSure'
}

export enum OnboardingReadiness {
  Absolutely = 'Absolutely',
  HighlyLikely = 'HighlyLikely',
  NotSure = 'NotSure',
  No = 'No'
}

export enum TeamImpactOption {
  ReliablePredictableWorkloads = 'ReliablePredictableWorkloads',
  GrowTopProfessionals = 'GrowTopProfessionals',
  LessStressMoreFun = 'LessStressMoreFun',
  QualityOfLifeImproves = 'QualityOfLifeImproves',
  PrideInPositiveImpact = 'PrideInPositiveImpact'
}

export enum CommissionSavingsOption {
  GenerateMorePresoldOrders = 'GenerateMorePresoldOrders',
  PassRatesToCustomers = 'PassRatesToCustomers',
  BuildLocalBrand = 'BuildLocalBrand',
  DriveDineInVolume = 'DriveDineInVolume',
  FinancialSuccessAndTeamPride = 'FinancialSuccessAndTeamPride',
  EnhanceCommunityHealth = 'EnhanceCommunityHealth'
}

/** Mirrors the backend ProducerOnboardingV1 (camelCase on the wire); dates are ISO strings. */
export interface ProducerOnboardingV1 {
  producerGuid: string;
  status: ProducerStatus;
  mealsPerDayAverage: MealsPerDayAverage | null;
  mealsPerDayBest: MealsPerDayBest | null;
  additionalPresoldCapacity: AdditionalPresoldCapacity | null;
  googleYelpRating: GoogleYelpRating | null;
  ratingImpactWillingness: RatingImpactWillingness | null;
  cleanIngredientsWillingness: CleanIngredientsWillingness | null;
  certifiedItemCountComfort: CertifiedItemCountComfort | null;
  readiness: OnboardingReadiness | null;
  termsAcceptedOn: string | null;
  termsVersion: string | null;
  teamImpactSelections: TeamImpactOption[];
  commissionSavingsSelections: CommissionSavingsOption[];
}

const MEALS_PER_DAY_AVERAGE_LABELS: Record<MealsPerDayAverage, string> = {
  [MealsPerDayAverage.Range0To50]: '0–50',
  [MealsPerDayAverage.Range50To100]: '50–100',
  [MealsPerDayAverage.Range100To150]: '100–150',
  [MealsPerDayAverage.Range150To200]: '150–200',
  [MealsPerDayAverage.Over200]: 'Over 200'
};

const MEALS_PER_DAY_BEST_LABELS: Record<MealsPerDayBest, string> = {
  [MealsPerDayBest.Range150To200]: '150–200',
  [MealsPerDayBest.Range200To250]: '200–250',
  [MealsPerDayBest.Range250To300]: '250–300',
  [MealsPerDayBest.Range300To400]: '300–400',
  [MealsPerDayBest.Over400]: 'Over 400'
};

const ADDITIONAL_PRESOLD_CAPACITY_LABELS: Record<AdditionalPresoldCapacity, string> = {
  [AdditionalPresoldCapacity.UpTo100]: 'Up to 100',
  [AdditionalPresoldCapacity.Range100To200]: '100–200',
  [AdditionalPresoldCapacity.Range200To300]: '200–300',
  [AdditionalPresoldCapacity.Range300To400]: '300–400',
  [AdditionalPresoldCapacity.Over400]: 'Over 400'
};

const GOOGLE_YELP_RATING_LABELS: Record<GoogleYelpRating, string> = {
  [GoogleYelpRating.FourFiveOrHigher]: '4.5 or higher',
  [GoogleYelpRating.FourZeroToFourFour]: '4.0–4.4',
  [GoogleYelpRating.BelowFourZero]: 'Below 4.0',
  [GoogleYelpRating.NotListed]: 'Not listed'
};

const RATING_IMPACT_WILLINGNESS_LABELS: Record<RatingImpactWillingness, string> = {
  [RatingImpactWillingness.GreatlyEnhance]: 'Greatly enhance',
  [RatingImpactWillingness.StayAboutTheSame]: 'Stay about the same'
};

const CLEAN_INGREDIENTS_WILLINGNESS_LABELS: Record<CleanIngredientsWillingness, string> = {
  [CleanIngredientsWillingness.AlreadyCookThisWay]: 'Already cook this way',
  [CleanIngredientsWillingness.FewSmallSwaps]: 'A few small swaps',
  [CleanIngredientsWillingness.OpenWithWork]: 'Open to it with some work',
  [CleanIngredientsWillingness.NotRightNow]: 'Not right now'
};

const CERTIFIED_ITEM_COUNT_COMFORT_LABELS: Record<CertifiedItemCountComfort, string> = {
  [CertifiedItemCountComfort.EightToTwelveFeelsRight]: '8–12 feels right',
  [CertifiedItemCountComfort.CouldDoMoreThanTwelve]: 'Could do more than 12',
  [CertifiedItemCountComfort.TooMany]: "That's too many",
  [CertifiedItemCountComfort.NotSure]: 'Not sure'
};

const READINESS_LABELS: Record<OnboardingReadiness, string> = {
  [OnboardingReadiness.Absolutely]: 'Absolutely',
  [OnboardingReadiness.HighlyLikely]: 'Highly likely',
  [OnboardingReadiness.NotSure]: 'Not sure',
  [OnboardingReadiness.No]: 'No'
};

const TEAM_IMPACT_LABELS: Record<TeamImpactOption, string> = {
  [TeamImpactOption.ReliablePredictableWorkloads]: 'Reliable, predictable workloads',
  [TeamImpactOption.GrowTopProfessionals]: 'Grow top professionals',
  [TeamImpactOption.LessStressMoreFun]: 'Less stress, more fun',
  [TeamImpactOption.QualityOfLifeImproves]: 'Quality of life improves',
  [TeamImpactOption.PrideInPositiveImpact]: 'Pride in positive impact'
};

const COMMISSION_SAVINGS_LABELS: Record<CommissionSavingsOption, string> = {
  [CommissionSavingsOption.GenerateMorePresoldOrders]: 'Generate more pre-sold orders',
  [CommissionSavingsOption.PassRatesToCustomers]: 'Pass rates to customers',
  [CommissionSavingsOption.BuildLocalBrand]: 'Build local brand',
  [CommissionSavingsOption.DriveDineInVolume]: 'Drive dine-in volume',
  [CommissionSavingsOption.FinancialSuccessAndTeamPride]: 'Financial success and team pride',
  [CommissionSavingsOption.EnhanceCommunityHealth]: 'Enhance community health'
};

/** A single-answer question: which field it reads and how each enum value renders. */
export interface OnboardingSingleQuestion {
  readonly key: keyof ProducerOnboardingV1;
  readonly question: string;
  readonly labels: Record<string, string>;
}

/** A multiselect question (array field). */
export interface OnboardingMultiQuestion {
  readonly key: 'teamImpactSelections' | 'commissionSavingsSelections';
  readonly question: string;
  readonly labels: Record<string, string>;
}

export const ONBOARDING_SINGLE_QUESTIONS: readonly OnboardingSingleQuestion[] = [
  { key: 'mealsPerDayAverage', question: 'Meals produced on an average day', labels: MEALS_PER_DAY_AVERAGE_LABELS },
  { key: 'mealsPerDayBest', question: 'Meals produced on the best day of the year', labels: MEALS_PER_DAY_BEST_LABELS },
  {
    key: 'additionalPresoldCapacity',
    question: 'Additional meals/day if the majority were pre-sold',
    labels: ADDITIONAL_PRESOLD_CAPACITY_LABELS
  },
  { key: 'googleYelpRating', question: 'Current Google / Yelp rating', labels: GOOGLE_YELP_RATING_LABELS },
  {
    key: 'ratingImpactWillingness',
    question: 'Willingness to let planning improve results',
    labels: RATING_IMPACT_WILLINGNESS_LABELS
  },
  {
    key: 'cleanIngredientsWillingness',
    question: 'Willingness to embrace clean ingredients',
    labels: CLEAN_INGREDIENTS_WILLINGNESS_LABELS
  },
  {
    key: 'certifiedItemCountComfort',
    question: 'Comfort carrying 8–12 Certified items',
    labels: CERTIFIED_ITEM_COUNT_COMFORT_LABELS
  },
  { key: 'readiness', question: 'Readiness to become a Healthy Gourmet Kitchen', labels: READINESS_LABELS }
];

export const ONBOARDING_MULTI_QUESTIONS: readonly OnboardingMultiQuestion[] = [
  {
    key: 'teamImpactSelections',
    question: 'How full pre-sold capacity would impact the team',
    labels: TEAM_IMPACT_LABELS
  },
  {
    key: 'commissionSavingsSelections',
    question: 'How commission savings would help the business',
    labels: COMMISSION_SAVINGS_LABELS
  }
];
